import React, { useContext, useEffect, useState, useRef } from 'react'
import { GlobalTable, HeaderLine, Td, TdButtons, Th, Tr } from '../../../../components/styles/table-style'
import {columnsServices2} from '../utils'
import { ServicesContext, ServicesObjectType } from '../../../../contexts/ServicesContext'
import ModalContext from '../../../../contexts/ModalContext'
import { QRCodeContext } from '../../../../contexts/QRCodeContext'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { ProductionOutletType } from '..'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import axios from '../../../../config/axios'
import { getStatusText } from '../../SteelOrders/SteelOrdersTable/utils'
import ActionsDropdown from './ActionsDropdown'
import { MoreHorizontal } from 'lucide-react'
import LoadMoreButton from '../../../../components/LoadMoreButton'

const ServicesTable2: React.FC = () => {
    const { setModalSection, setModalActive } = useContext(ModalContext)
    const { setService } = useContext(ServicesContext)
    const { setContentJson } = useContext(QRCodeContext)
    
    const [servicesList, setServicesList] = useState<ServicesObjectType[]>([])
    const [totalServices, setTotalServices] = useState<number>(0)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [hasNextPage, setHasNextPage] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
    const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})
    
    const { setTabIndex, search, appliedFilters } = useOutletContext<ProductionOutletType>()
    const navigate = useNavigate()
    
    const ITEMS_PER_PAGE = 15
    const getNextStage = (serviceStages: any[]) => {
        if (!serviceStages || serviceStages.length === 0) return 'Sem estágios'
        const nextStageIndex = serviceStages.findIndex((el: any) => !el.completed)
        if (nextStageIndex === -1) return 'Todos concluídos'
        const stage = serviceStages[nextStageIndex]
        return stage?.Stage?.name || stage?.stage?.name || 'Estágio indefinido'
    }
    const fetchServices = async (page: number = 1, searchTerm: string = '', filters: Filter[] = [], append: boolean = false) => {
        setIsLoading(true)
        setError(null)
        
        try {
          const params: any = {
            page,
            limit: ITEMS_PER_PAGE
          }
    
          if (searchTerm && searchTerm.trim() !== '') {
            params.search = searchTerm.trim()
          }
    
          if (filters.length > 0) {
            const filterObj: any = {}
            filters.forEach(filter => {
              if (filter.value !== undefined && filter.value !== null && String(filter.value).trim() !== '') {
                filterObj[filter.key] = {
                  value: filter.value,
                  filterType: filter.filterType || 'Contém'
                }
              }
            })
    
            if (Object.keys(filterObj).length > 0) {
              params.filters = JSON.stringify(filterObj)
            }
          }
    
          const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/services/paginated`, {
            params
          })
    
          const data = response.data
          
          if (append) {
            setServicesList(prev => [...prev, ...(data.data || [])])
          } else {
            setServicesList(data.data || [])
          }
          
          setTotalServices(data.total || 0)
          setCurrentPage(page)
          setHasNextPage((data.data?.length || 0) === ITEMS_PER_PAGE && (page * ITEMS_PER_PAGE) < (data.total || 0))
          
        } catch (err: any) {
          if (!append) {
            setServicesList([])
            setTotalServices(0)
            setHasNextPage(false)
          }
        } finally {
          setIsLoading(false)
        }
      }
      const [debouncedSearch, setDebouncedSearch] = useState<string>('')
      useEffect(() => {
        const timeoutId = setTimeout(() => {
          setDebouncedSearch(search || '')
        }, 300)
        
        return () => clearTimeout(timeoutId)
      }, [search])
    
      useEffect(() => {
        setCurrentPage(1)
        setServicesList([])
        fetchServices(1, debouncedSearch, appliedFilters || [], false)
      }, [debouncedSearch, appliedFilters])
    
      const handleLoadMore = () => {
        if (!isLoading && hasNextPage) {
          const nextPage = currentPage + 1
          fetchServices(nextPage, debouncedSearch, appliedFilters || [], true)
        }
      }

    const handleViewService = (service: ServicesObjectType) => {
        setService(service)
        setModalSection('viewOPServiceModal')
        setModalActive(true)
    }

    const handleEditService = (service: ServicesObjectType) => {
        navigate(`/panel/production/edit/${service.id}`)
    }

    const handleGenerateRNC = (service: ServicesObjectType) => {
        setService(service)
        setModalSection('generateRNCModal')
        setModalActive(true)
    }

    const handleDeleteService = async (service: ServicesObjectType) => {
        if (window.confirm(`Tem certeza que deseja excluir ${service.codeOfOP}?`)) {
          try {
            await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${service.id}`)
            fetchServices(1, debouncedSearch, appliedFilters || [], false)
          } catch (error) {
            console.error('Erro ao excluir serviço:', error)
            alert('Erro ao excluir serviço')
          }
        }
    }

    const handleToggleDropdown = (serviceId: string) => {
        setOpenDropdownId(prev => prev === serviceId ? null : serviceId)
    }

    const handleCloseDropdown = () => {
        setOpenDropdownId(null)
    }

    return (
        <>
          <GlobalTable>
            <HeaderLine>
              {columnsServices2.map((column) => (
                <Th key={column}>{column}</Th>
              ))}
              <Th key={0}></Th>
            </HeaderLine>
            <tbody>   
              {servicesList.map((service: any) => (
                <Tr key={service.id}>
                  <Td>{service.codeOfOP || 'N/A'}</Td>
                  <Td>{service.OrderItem?.ploomesProductName || '-'}</Td>
                  <Td>{service.OrderItem?.Order?.saleNumberPloomes || 'N/A'}</Td>
                  <Td>{service.OrderItem?.weight || '-'}</Td>
                  <Td>{getStatusText(service.status)}</Td>
                  <Td>{getNextStage(service.Service_Stage || [])}</Td>
                  <Td>
                    {(service.OrderItem?.Order?.Company_Order_clientIdToCompany?.companyName || 'Cliente não informado').split(' ').slice(0,2).join(' ')}
                  </Td>
                  <TdButtons>
                    <div style={{ position: 'relative' }}>
                      <button
                        ref={(el) => {
                          buttonRefs.current[service.id] = el
                        }}
                        onClick={() => handleToggleDropdown(service.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <MoreHorizontal size={18} />
                      </button>
                      {openDropdownId === service.id && (
                        <ActionsDropdown
                          buttonRef={buttonRefs.current[service.id]}
                          onView={() => handleViewService(service)}
                          onEdit={() => handleEditService(service)}
                          onGenerateRNC={() => handleGenerateRNC(service)}
                          onDelete={() => handleDeleteService(service)}
                          onClose={handleCloseDropdown}
                        />
                      )}
                    </div>
                  </TdButtons>
                </Tr>
              ))}
            </tbody>
          </GlobalTable>
          
          {hasNextPage && (
            <LoadMoreButton
              onLoadMore={handleLoadMore}
              isLoading={isLoading}
              hasMore={hasNextPage}
              currentCount={servicesList.length}
              totalCount={totalServices}
            />
          )}
        </>
    )
}

export default ServicesTable2;